import { useState, useMemo } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Select, MenuItem, FormControl, InputLabel, Pagination, Grid
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import CustomLinearProgress from '../Loaders/CustomLinearProgress';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import CustomInput from '../Inputs/CustomInput';

const ShipmentsProductsTable = (props) => {
  const { theme } = useTheme();

  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  const [filters, setFilters] = useState({
    searchTerm: '',
    status: '',
    dateRange: ''
  });

  const dataExample = [{
    inboundOrderId: "",
    shipmentId: "",
    shipToAddress: {
      fcName: "",
      addressLine1: "",
      city: "",
      stateCode: "",
      countryCode: "",
      postalCode: ""
    },
    returnAddress: {
      addressLine1: "",
      city: "",
      stateCode: "",
      countryCode: "",
      postalCode: ""
    },
    status: "PENDING_SHIPMENT_DETAILS",
    shipmentStatus: "PENDING_SHIPMENT_DETAILS",
    createdDate: "2025-02-19T13:32:54.767Z",
    shipmentUnits: 16,
    receivedUnits: 0,
    expectedDeliveryDate: "2025-03-05T00:00:00.000Z",
    updatedExpectedDeliveryDate: "2025-03-05T00:00:00.000Z",
    receivedUnitsAtFC: 0,
    poType: "WFS",
    shipmentCarrierType: "NONE",
    isExceptionOccurred: false,
    isPOBoxEnabled: false
  }];

  const filteredAndPaginatedData = useMemo(() => {
    const shipments = props.shipments || [];

    let filtered = shipments.filter(shipment => {
      const fixedSearchTerm = filters.searchTerm.trim().toLowerCase();

      const matchesSearch = fixedSearchTerm === '' ||
        shipment.inboundOrderId.toLowerCase().includes(fixedSearchTerm) ||
        shipment.shipmentId.toLowerCase().includes(fixedSearchTerm) ||
        shipment.shipToAddress.addressLine1.toLowerCase().includes(fixedSearchTerm);

      const matchesStatus = filters.status === '' || shipment.status === filters.status;

      const matchesDateRange = filters.dateRange === '' || (() => {
        const shipmentDate = new Date(shipment.createdDate);
        const now = new Date();

        const dayMS = 24 * 60 * 60 * 1000;

        switch (filters.dateRange) {
          case 'last7days':
            return shipmentDate >= new Date(now.getTime() - 7 * dayMS);
          case 'last30days':
            return shipmentDate >= new Date(now.getTime() - 30 * dayMS);
          case 'last90days':
            return shipmentDate >= new Date(now.getTime() - 90 * dayMS);
          case 'last180days':
            return shipmentDate >= new Date(now.getTime() - 180 * dayMS);
          case 'last365days':
            return shipmentDate >= new Date(now.getTime() - 365 * dayMS);
          default:
            return true;
        }
      })();

      return matchesSearch && matchesStatus && matchesDateRange;
    });

    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      totalItems,
      totalPages
    };
  }, [props.shipments, filters, page]);

  const uniqueStatuses = useMemo(() => {
    const shipments = props.shipments || [];
    const statuses = [...new Set(shipments.map(shipment => shipment.status))];
    return statuses.filter(status => status && status.trim() !== '');
  }, [props.shipments]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      status: '',
      dateRange: ''
    });
    setPage(1);
  };

  const styles = {
    th: {
      fontWeight: "bold",
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 16,
      position: "sticky",
      top: 0,
      backgroundColor: theme.background.secondary,
      zIndex: 1,
      py: '10px',
    },
    td: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 14,
      py: '5px'
    },
    chip: {
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      width: 'fit-content',
      px: '10px',
    },
    filterContainer: {
      backgroundColor: theme.background.tertiary,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '8px',
      p: 2,
      mb: 2,
    },
    filterInput: {
      '& .MuiOutlinedInput-root': {
        fontFamily: theme.fontFamily,
        fontSize: 14,
        backgroundColor: theme.background.secondary,
        '& fieldset': {
          borderColor: theme.border.light,
        },
        '&:hover fieldset': {
          borderColor: theme.border.medium,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.color.primary,
        },
      },
      '& .MuiInputLabel-root': {
        fontFamily: theme.fontFamily,
        color: theme.text.secondary,
        '&.Mui-focused': {
          color: theme.color.primary,
        },
      },
      '& .MuiSelect-select': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      mt: 2,
      '& .MuiPaginationItem-root': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
        '&.Mui-selected': {
          backgroundColor: theme.color.primary,
          color: theme.text.primary,
        },
        '&:hover': {
          backgroundColor: theme.background.tertiary,
        },
      },
    },
    filterButton: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.border.light}`,
      borderRadius: '20px',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: theme.background.tertiary,
      },
    },
    clearButton: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.accent.error}`,
      borderRadius: '20px',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: theme.accent.error,
        color: theme.text.negative,
      },
    },
    resultsText: {
      fontFamily: theme.fontFamily,
      color: theme.text.secondary,
      fontSize: 14,
      mb: 1,
    }
  };

  return (
    <Box m={2}>
      <Box sx={styles.filterContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <CustomInput
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Search by Order ID, Shipment ID, or Address"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: theme.text.secondary, mr: 1, fontSize: 22, fontFamily: theme.fontFamily }} />
              }}
              sx={styles.filterInput}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={styles.filterInput}>
              <InputLabel sx={{ fontFamily: theme.fontFamily }}>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="" sx={{ fontFamily: theme.fontFamily }}>All Statuses</MenuItem>
                {uniqueStatuses.map((status) => (
                  <MenuItem key={status} value={status} sx={{ fontFamily: theme.fontFamily }}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={styles.filterInput}>
              <InputLabel sx={{ fontFamily: theme.fontFamily }}>Date Range</InputLabel>
              <Select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                label="Date Range"
              >
                <MenuItem value="" sx={{ fontFamily: theme.fontFamily }}>All Dates</MenuItem>
                <MenuItem value="last7days" sx={{ fontFamily: theme.fontFamily }}>Last 7 Days</MenuItem>
                <MenuItem value="last30days" sx={{ fontFamily: theme.fontFamily }}>Last 30 Days</MenuItem>
                <MenuItem value="last90days" sx={{ fontFamily: theme.fontFamily }}>Last 90 Days</MenuItem>
                <MenuItem value="last180days" sx={{ fontFamily: theme.fontFamily }}>Last 180 Days</MenuItem>
                <MenuItem value="last365days" sx={{ fontFamily: theme.fontFamily }}>Last 365 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              onClick={clearFilters}
              variant="outlined"
              size="small"
              sx={styles.clearButton}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Typography sx={styles.resultsText}>
        Showing {filteredAndPaginatedData.data.length} of {filteredAndPaginatedData.totalItems} shipments
        {filteredAndPaginatedData.totalPages > 1 && ` (Page ${page} of ${filteredAndPaginatedData.totalPages})`}
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'auto',
          backgroundColor: theme.background.secondary,
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.background.primary,
            borderRadius: '3px',
            border: `1px solid ${theme.border.light}`,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.scrollbar,
            borderRadius: '15px',
            cursor: 'grab',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.scrollbarHover,
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.th}>Created Date</TableCell>
              <TableCell sx={styles.th}>Expected Delivery Date</TableCell>
              <TableCell sx={styles.th}>Order ID</TableCell>
              <TableCell sx={styles.th}>Shipment ID</TableCell>
              <TableCell sx={styles.th}>Ship to Address</TableCell>
              <TableCell sx={styles.th}>Status</TableCell>
              <TableCell sx={styles.th}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <CustomLinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              filteredAndPaginatedData.data.length > 0 && filteredAndPaginatedData.data.map((shipment, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography sx={{ ...styles.td, width: '10vw' }}>
                      {new Date(shipment.createdDate).toDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ ...styles.td, width: '10vw' }}>
                      {new Date(shipment.updatedExpectedDeliveryDate || shipment.expectedDeliveryDate).toDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ ...styles.td, width: '15vw' }}>
                      {shipment.inboundOrderId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ ...styles.td, width: '8vw' }}>
                      {shipment.shipmentId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <Box>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            Address line: {shipment.shipToAddress.addressLine1}
                          </Typography>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            City: {shipment.shipToAddress.city}
                          </Typography>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            Country Code: {shipment.shipToAddress.countryCode}
                          </Typography>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            Postal Code: {shipment.shipToAddress.postalCode}
                          </Typography>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            State Code: {shipment.shipToAddress.stateCode}
                          </Typography>
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            FC: {shipment.shipToAddress.fcName}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -15],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography sx={{ ...styles.td, width: '15vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', }}>
                        {shipment.shipToAddress.addressLine1}
                      </Typography>
                    </HtmlTooltip>
                  </TableCell>
                  <TableCell>
                    <Typography sx={styles.td}>
                      {shipment.status}
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.td}>
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12, cursor: "pointer", }}>
                          click to see in walmart seller center
                        </Typography>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -15],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => window.open(`https://seller.walmart.com/wfs/shipments/order-details/${shipment.inboundOrderId}`)}
                        sx={{
                          textTransform: 'none',
                          fontFamily: theme.fontFamily,
                          color: theme.color.primary,
                          borderRadius: '20px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.border.light}`
                        }}
                      >
                        <InfoIcon />
                      </Button>
                    </HtmlTooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
            {(!props.isLoading && filteredAndPaginatedData.data.length === 0) && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ width: '100%', textAlign: 'center', }}>
                    <Typography sx={styles.td}>
                      {filteredAndPaginatedData.totalItems === 0 ? 'No Shipments found!' : 'No shipments match your filters!'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredAndPaginatedData.totalPages > 1 && (
        <Box sx={styles.pagination}>
          <Pagination
            count={filteredAndPaginatedData.totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="medium"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default ShipmentsProductsTable;